import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type { ConsentState } from './types';
import { readConsent, writeConsent } from './store';

export interface ConsentApi {
    consent: ConsentState;
    /** Grant both categories and dismiss the banner. */
    acceptAll(): void;
    /** Deny both categories and dismiss the banner. */
    rejectAll(): void;
    /** Merge an explicit category choice; any choice counts as a decision. */
    setCategories(partial: Partial<Pick<ConsentState, 'analytics' | 'sessionReplay' | 'errorMonitoring'>>): void;
    /** Re-show the banner (used by "manage consent") without changing categories. */
    reopen(): void;
    /** Persist an explicit category choice, then reload so Statsig re-boots with
     *  the matching plugin set (a live Statsig client's plugins can't be swapped)
     *  and Sentry starts/stops to match the new error-monitoring choice. */
    commitAndReload(categories: { analytics: boolean; sessionReplay: boolean; errorMonitoring: boolean }): void;
}

const ConsentContext = createContext<ConsentApi | null>(null);

/** App-wide consent state. Reads localStorage once on mount; every mutation persists. */
export function ConsentProvider({ children }: { children: ReactNode }) {
    const [consent, setConsent] = useState<ConsentState>(() => readConsent());

    const commit = useCallback((next: ConsentState) => {
        setConsent(next);
        writeConsent(next);
    }, []);

    const acceptAll = useCallback(() => {
        commit({ analytics: true, sessionReplay: true, errorMonitoring: true, decided: true });
    }, [commit]);

    const rejectAll = useCallback(() => {
        commit({ analytics: false, sessionReplay: false, errorMonitoring: false, decided: true });
    }, [commit]);

    const setCategories = useCallback(
        (partial: Partial<Pick<ConsentState, 'analytics' | 'sessionReplay' | 'errorMonitoring'>>) => {
            setConsent(prev => {
                const next: ConsentState = { ...prev, ...partial, decided: true };
                writeConsent(next);
                return next;
            });
        },
        [],
    );

    const reopen = useCallback(() => {
        setConsent(prev => {
            const next: ConsentState = { ...prev, decided: false };
            writeConsent(next);
            return next;
        });
    }, []);

    const commitAndReload = useCallback(
        (categories: { analytics: boolean; sessionReplay: boolean; errorMonitoring: boolean }) => {
            writeConsent({ ...categories, decided: true });
            if (typeof window !== 'undefined') window.location.reload();
        },
        [],
    );

    const api = useMemo<ConsentApi>(
        () => ({ consent, acceptAll, rejectAll, setCategories, reopen, commitAndReload }),
        [consent, acceptAll, rejectAll, setCategories, reopen, commitAndReload],
    );

    return <ConsentContext.Provider value={api}>{children}</ConsentContext.Provider>;
}

/** Access consent state + mutators. Must be used under <ConsentProvider>. */
export function useConsent(): ConsentApi {
    const ctx = useContext(ConsentContext);
    if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
    return ctx;
}
